import React from "react";
import Footer from "../components/Footer";
import { 
  Container, 
  Typography, 
  Box, 
  Paper,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  SafetyCheck as SafetyIcon,
  DirectionsBike as ScooterIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Security as ShieldIcon,
  DirectionsBike as HelmetIcon,
  PlayArrow as PlayIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { deepPurple } from '@mui/material/colors';

import Navbar from "../components/Navbar";

const YOUTUBE_VIDEO_ID = "a3pfwNoKVIY";
const YOUTUBE_VIDEO_URL = `https://www.youtube.com/watch?v=${YOUTUBE_VIDEO_ID}`;
const YOUTUBE_THUMBNAIL_URL = `https://img.youtube.com/vi/${YOUTUBE_VIDEO_ID}/maxresdefault.jpg`;

const Safety_Precautions = () => {
  const segment1Intro = "As a rider, you are in full control of your scooter and your safety. It is important to make sure you are alert, aware, and ready to take on the responsibility of riding.";
  const segment1Tips = [
    "Never ride a scooter under the influence. Riding under the influence increases the risk of injury when riding and impairs your ability to ride safely.",
    "Before riding, inspect your scooter to make sure it is in good working order. If your scooter is broken, has missing parts, or is otherwise unfit for riding, do not ride the scooter. Return it to the ScooterLab check-in center for a replacement.",
    "Lastly, consider your environment. Look out for puddles, leaves, or other debris that may be hazardous. Dry asphalt is the most stable surface to ride on. Avoid unstable metal surfaces, especially slick metal.",
  ];
  const segment1Closing = "Once you, your scooter, and your environment are in proper working order, you are ready to ride!";

  const segment2Tips = [
    "While riding, always wear a properly fitted helmet.",
    "Follow all traffic laws. Share the streets with other motorists, cyclists, and pedestrians. Be mindful of others, and pay attention to your surroundings.",
    "Avoid wearing earbuds or headphones, as they will impair your hearing ability.",
    "Counteract forces to safely brake or turn. For example, if you need to brake as you are riding, slightly bend your knees and lean back. This will prevent you from falling forward. If you are turning, counteract your body weight in the opposite direction you are turning. If you are turning left, slightly lean right. If turning right, lean left.",
    "Keep both hands on the handlebars. This gives you more control over your scooter and keeps you stabilized. Do not look at your phone or carry items while riding.",
    "Be cautious around different terrains. If possible, stick to riding on clean, dry, solid pavement. This surface is the safest to ride on. Avoid leaves, dirt, sand, or wet metal. If you must ride on surfaces other than clean, dry pavement, avoid sudden turns or braking, if possible. Stay steady and avoid maneuvering until you get to the pavement.",
  ];

  const segment3Tips = [
    "While riding, avoid standing with your feet side by side. This foot stance gives you less control over your scooter should you need to brake suddenly or turn. Instead, stand with one foot in front of the other. Choose a natural and comfortable foot stance.",
    "Regular vs. Goofy: If your left foot naturally goes forward, this is the regular stance. If your right foot naturally goes forward, this is called goofy stance. Either stance is acceptable when riding. Just make sure that you are comfortable enough to maneuver your scooter.",
    "Keep your knees slightly bent. While you are riding, it is important to never lock your knees. Keeping your knees slightly bent will help you counteract the forces of riding and will help you brake.",
  ];

  const segment4Tips = [
    "Locking up your scooter is an important part of security. Your scooter is your property during the duration of its checkout, and it is your responsibility to make sure it is secured properly.",
    "If you are unsure of a safe place to store your scooter, hold off on locking it up if you can. If you are on either Main, Downtown, or San Pedro I, find the nearest ScooterLab storage facility to store your scooter until you are ready to ride again.",
    "If you are not near any UTSA campus, find a sturdy secure place to lock your scooter. Use a U-lock on the wheel, especially if you will be away from your scooter for more than 15 minutes.",
    "It is not recommended to leave your scooter unattended for more than 30 minutes to an hour, as this increases the chances of theft.",
  ];

  const segment5Tips = [
    "Park in well-lit, frequented areas.",
    "Avoid parking your scooter on any walkways, sidewalks, driveways, or streets.",
    "Look for designated scooter parking zones, like the one in downtown.",
  ];

  const segment6Tips = [
    "When you are ready to return your scooter, simply find a ScooterLab check-in facility, located at San Pedro I campus.",
    "Follow the check-in procedure to ensure that you fully complete any necessary tasks.",
    "Look out for any follow-up emails, questionnaires, or surveys following your ScooterLab participation! These are important parts of the study and are helpful in obtaining data about riders like you.",
  ];

  const renderSegment = (title, icon, intro, tips, closing) => (
    <Box sx={{ mb: 6 }}>
      <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2, display: 'flex', alignItems: 'center', color: deepPurple[700] }}>
        {icon}
        <Box component="span" sx={{ ml: 1 }}>{title}</Box>
      </Typography>
      {intro && (
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 2 }}>
          {intro}
        </Typography>
      )}
      <List dense sx={{ width: '100%' }}>
        {tips.map((tip, index) => (
          <ListItem key={index} sx={{ px: 0, alignItems: 'flex-start' }}>
            <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
              <CheckIcon color="success" fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={tip} primaryTypographyProps={{ variant: 'body1' }} />
          </ListItem>
        ))}
      </List>
      {closing && (
        <Typography variant="body1" sx={{ color: "text.secondary", mt: 1, fontStyle: 'italic' }}>
          {closing}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          position: 'relative'
        }}>
          <Navbar />
    <Container maxWidth="lg" sx={{ pt: 10, mt: 4, mb: 6, flex: 1, pb: 8 }}>
      {/* Hero Section */}
      <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden', mb: 6, backgroundColor: deepPurple[50] }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', p: 6 }}>
          <Avatar sx={{ bgcolor: deepPurple[500], mb: 3, width: 80, height: 80 }}>
            <SafetyIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h3" sx={{ fontWeight: "bold", mb: 2, color: deepPurple[800] }}>
            Safety Precautions
          </Typography>
          <Typography variant="h6" sx={{ color: "text.secondary", maxWidth: '700px' }}>
            Your safety is our top priority. Follow these guidelines to ensure a safe and enjoyable riding experience.
          </Typography>
        </Box>
      </Paper>

      {/* YouTube Video Section - opens on YouTube (embedding disabled by owner) */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          mb: 6,
          cursor: 'pointer',
          transition: 'box-shadow 0.2s',
          '&:hover': { boxShadow: 6 },
        }}
        component="a"
        href={YOUTUBE_VIDEO_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', bgcolor: '#000' }}>
          <Box
            component="img"
            src={YOUTUBE_THUMBNAIL_URL}
            alt="ScooterLab Safety Video"
            onError={(e) => { e.target.src = `https://img.youtube.com/vi/${YOUTUBE_VIDEO_ID}/hqdefault.jpg`; }}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0,0,0,0.35)',
            }}
          >
            <Avatar sx={{ width: 72, height: 72, bgcolor: 'rgba(255,255,255,0.9)' }}>
              <PlayIcon sx={{ fontSize: 48, color: deepPurple[700], ml: 0.5 }} />
            </Avatar>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: deepPurple[50] }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", color: deepPurple[700] }}>
            ScooterLab Safety Video
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: deepPurple[600] }}>
            Watch on YouTube
            <OpenInNewIcon fontSize="small" />
          </Typography>
        </Box>
      </Paper>

      <Divider sx={{ my: 4 }} />

      {/* Segment 1: Tips Before Riding */}
      <Paper elevation={1} sx={{ p: 4, borderRadius: 3, mb: 4 }}>
        {renderSegment("Segment 1: Tips Before Riding", <WarningIcon color="warning" sx={{ mr: 1 }} />, segment1Intro, segment1Tips, segment1Closing)}
      </Paper>

      {/* Segment 2: Tips While Riding */}
      <Paper elevation={1} sx={{ p: 4, borderRadius: 3, mb: 4 }}>
        {renderSegment("Segment 2: Tips While Riding", <ScooterIcon color="primary" sx={{ mr: 1 }} />, null, segment2Tips, null)}
      </Paper>

      {/* Segment 3: The Scooter Stance */}
      <Paper elevation={1} sx={{ p: 4, borderRadius: 3, mb: 4 }}>
        {renderSegment("Segment 3: The Scooter Stance", <HelmetIcon color="action" sx={{ mr: 1 }} />, null, segment3Tips, null)}
      </Paper>

      {/* Segment 4: Locks and Security */}
      <Paper elevation={1} sx={{ p: 4, borderRadius: 3, mb: 4 }}>
        {renderSegment("Segment 4: Locks and Security", <ShieldIcon color="primary" sx={{ mr: 1 }} />, null, segment4Tips, null)}
      </Paper>

      {/* Segment 5: Parking */}
      <Paper elevation={1} sx={{ p: 4, borderRadius: 3, mb: 4 }}>
        {renderSegment("Segment 5: Parking", <CheckIcon color="success" sx={{ mr: 1 }} />, null, segment5Tips, null)}
      </Paper>

      {/* Segment 6: Returning the Scooter */}
      <Paper elevation={1} sx={{ p: 4, borderRadius: 3, mb: 4 }}>
        {renderSegment("Segment 6: Returning the Scooter", <SafetyIcon color="primary" sx={{ mr: 1 }} />, null, segment6Tips, null)}
      </Paper>
    </Container>
    <Box sx={{ 
            width: '100%',
            bgcolor: 'background.paper',
            boxShadow: '0 -2px 5px rgba(0,0,0,0.1)',
            position: 'relative', // Changed from fixed to relative
            mt: 'auto' // Pushes footer to bottom
          }}>
            <Footer />
          </Box>
        </Box>
  );
};

export default Safety_Precautions;